import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Skeleton } from "../../Components/Needed";
import {
    createCampaign, fetchAudienceOptions, fetchCouponOptions, fetchLanguageList, fetchMessages, fetchOverlap,
    getCampaign, reachAudience, setCampaignStatus, updateCampaign,
} from "../../Helpers/campaignApi";
import AudienceStep from "./AudienceStep";
import MessagesPanel from "./MessagesPanel";
import { CHANNELS, CHANNEL_LABEL, DAY_NAMES, EDITABLE, TRIGGER_LABEL, errorsText, reasonText, scheduleText } from "./shared";
import "../../Assets/css/notification-centre.css";
import "./campaigns.css";

// Create or change a campaign (Phase 8.9): audience -> channels -> messages -> coupon -> schedule ->
// review (who it reaches per channel, who is left out and why, what still needs fixing) -> start.
// The campaign is saved as a draft when you leave the first two steps, so its messages can be written.
const STEPS = ["Audience", "Channels", "Messages", "Coupon", "Schedule", "Review"];

const EMPTY = {
    name: "", description: "", purpose: "promotional", audienceType: "customer",
    segment: { logic: "AND", rules: [] }, uploadId: null, staffIds: [],
    channels: ["push"], channelPriority: "all", languages: ["en"],
    coupon: null, link: { url: "" },
    schedule: { type: "now", at: null, recurrence: { freq: "weekly", byDay: [1], time: "10:00" }, trigger: { event: "inactive_days", days: 30, coolDownDays: null }, endsAt: null },
    retry: { maxAttempts: 3, backoffMinutes: 15 }, attributionDays: 7, shadow: false,
};

// Browser date-time input <-> stored date.
const toLocalInput = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (value) => (value ? new Date(value).toISOString() : null);

const toPayload = (d) => ({
    name: d.name, description: d.description, purpose: d.purpose, audienceType: d.audienceType,
    segment: { logic: "AND", rules: d.segment?.rules || [] }, uploadId: d.uploadId || null, staffIds: d.staffIds || [],
    channels: d.channels, channelPriority: d.channelPriority, languages: d.languages,
    coupon: d.coupon?.couponCode ? { couponCode: d.coupon.couponCode } : null,
    link: { url: d.link?.url ? d.link.url.trim() : null },
    schedule: d.schedule, retry: d.retry, attributionDays: Number(d.attributionDays) || 7, shadow: !!d.shadow,
});

const CampaignWizard = () => {
    const { campaignId: routeId } = useParams();
    const navigate = useNavigate();
    const [campaignId, setCampaignId] = useState(routeId || null);
    const [draft, setDraftState] = useState(routeId ? null : EMPTY);
    const [status, setStatus] = useState("draft");
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);
    const [options, setOptions] = useState(null);
    const [languages, setLanguages] = useState([]);
    const [coupons, setCoupons] = useState(null);
    const [review, setReview] = useState(null);

    const setDraft = (patch) => setDraftState((prev) => ({ ...prev, ...patch }));
    const setSchedule = (patch) => setDraftState((prev) => ({ ...prev, schedule: { ...prev.schedule, ...patch } }));

    useEffect(() => {
        (async () => {
            const [opts, langs, cps] = await Promise.all([fetchAudienceOptions(), fetchLanguageList(), fetchCouponOptions()]);
            if (opts.ok) setOptions(opts.data);
            if (langs.ok) setLanguages((langs.data.languages || []).filter((l) => l.enabled !== false));
            if (cps.ok) setCoupons(cps.data.coupons || []);
        })();
    }, []);
    useEffect(() => {
        if (!routeId) return;
        (async () => {
            const result = await getCampaign(routeId);
            if (!result.ok) { toast.error(result.message); navigate("/campaigns"); return; }
            const c = result.data.campaign;
            setStatus(c.status);
            setDraftState({
                ...EMPTY, ...c,
                schedule: { ...EMPTY.schedule, ...c.schedule, recurrence: { ...EMPTY.schedule.recurrence, ...(c.schedule?.recurrence || {}) }, trigger: { ...EMPTY.schedule.trigger, ...(c.schedule?.trigger || {}) } },
                link: { url: c.link?.url || "" },
                coupon: c.coupon?.couponCode ? c.coupon : null,
            });
        })();
    }, [routeId, navigate]);

    const editable = EDITABLE.includes(status);

    // Saves the draft (creates it the first time). Returns true when saved.
    const save = useCallback(async () => {
        if (!editable) return true;
        setBusy(true);
        const payload = toPayload(draft);
        const result = campaignId ? await updateCampaign(campaignId, payload) : await createCampaign(payload);
        setBusy(false);
        if (!result.ok) { toast.error(errorsText(result)); return false; }
        const saved = result.data.campaign;
        if (!campaignId) {
            setCampaignId(saved.campaignId);
            window.history.replaceState(null, "", `/campaigns/${saved.campaignId}/edit`);
        }
        setDraftState((prev) => ({ ...prev, coupon: saved.coupon?.couponCode ? saved.coupon : null }));
        return true;
    }, [campaignId, draft, editable]);

    const goTo = async (next) => {
        if (next > step && step !== 2 && !(await save())) return;
        if (next >= 2 && !campaignId && !(await save())) return;
        setStep(next);
    };

    // The review: who it reaches today per channel, who is left out and why, the messages still to write.
    const loadReview = useCallback(async () => {
        if (!campaignId) return;
        setReview(null);
        const [reach, messages, overlap] = await Promise.all([
            draft.schedule.type === "trigger" ? Promise.resolve({ ok: true, data: null }) : reachAudience(toPayload(draft)),
            fetchMessages(campaignId),
            fetchOverlap(campaignId),
        ]);
        setReview({ reach: reach.ok ? reach.data : { error: reach.message }, problems: messages.ok ? messages.data.problems : [], overlap: overlap.ok ? overlap.data.warnings || [] : [] });
    }, [campaignId, draft]);
    useEffect(() => { if (step === 5) loadReview(); }, [step]); // eslint-disable-line

    const start = async () => {
        if (!(await save())) return;
        setBusy(true);
        const result = await setCampaignStatus(campaignId, "scheduled");
        setBusy(false);
        if (!result.ok) { toast.error(result.message); return; }
        (result.data.warnings || []).forEach((w) => toast.info(w.message));
        toast.success(draft.schedule.type === "now" ? "Started: it goes out within a minute" : "Scheduled");
        navigate(`/campaigns/${campaignId}`);
    };

    const coupon = useMemo(() => (draft?.coupon?.couponCode ? (coupons || []).find((c) => c.couponCode === draft.coupon.couponCode) || draft.coupon : null), [draft, coupons]);

    if (!draft) return <Skeleton height={320} />;
    const staff = draft.audienceType === "staff";
    const s = draft.schedule;

    return (
        <div className="settings-page">
            <div className="mb-3">
                <button type="button" className="btn btn-link btn-sm px-0" onClick={() => navigate(campaignId ? `/campaigns/${campaignId}` : "/campaigns")}>&larr; {campaignId ? "Back to the campaign" : "All campaigns"}</button>
                <h4 className="fw-semibold mb-1">{campaignId ? (draft.name || "Campaign") : "New campaign"}</h4>
                {!editable && <div className="alert alert-warning py-2 small mb-0">This campaign is {status}; it can't be changed. Pause it first, or duplicate it.</div>}
            </div>

            <ol className="cw-steps" aria-label="Steps">
                {STEPS.map((name, i) => (
                    <li key={name} className={`cw-step ${i === step ? "is-current" : i < step ? "is-done" : ""}`}>
                        <button type="button" aria-current={i === step ? "step" : undefined} onClick={() => goTo(i)} disabled={busy}>
                            <span className="cw-step-num" aria-hidden="true">{i < step ? "✓" : i + 1}</span>
                            <span className="cw-step-name">{name}</span>
                        </button>
                    </li>
                ))}
            </ol>

            <fieldset className="cw-card" disabled={!editable && step !== 2 && step !== 5}>
                {step === 0 && (
                    <>
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label className="form-label small" htmlFor="cw-name">Name</label>
                                <input id="cw-name" className="form-control" maxLength={120} value={draft.name} onChange={(e) => setDraft({ name: e.target.value })} placeholder="Diwali weekend offer" />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small" htmlFor="cw-purpose">Kind</label>
                                <select id="cw-purpose" className="form-select" value={draft.purpose} disabled={staff} onChange={(e) => setDraft({ purpose: e.target.value })}>
                                    <option value="promotional">Promotional (offers, news): needs marketing consent</option>
                                    <option value="service">Service (information about their account or trips)</option>
                                </select>
                            </div>
                            <div className="col-12">
                                <label className="form-label small" htmlFor="cw-desc">Note for the team (optional)</label>
                                <input id="cw-desc" className="form-control" maxLength={500} value={draft.description} onChange={(e) => setDraft({ description: e.target.value })} />
                            </div>
                        </div>
                        <AudienceStep draft={draft} setDraft={setDraft} options={options} trigger={s.type === "trigger"} />
                    </>
                )}

                {step === 1 && (
                    <>
                        <span className="form-label small d-block">Channels</span>
                        <div className="d-flex flex-wrap gap-3 mb-2">
                            {CHANNELS.map((ch) => {
                                const off = staff && ch !== "push";
                                return (
                                    <div className="form-check" key={ch}>
                                        <input className="form-check-input" type="checkbox" id={`cw-ch-${ch}`} disabled={off} checked={draft.channels.includes(ch)} onChange={() => setDraft({ channels: draft.channels.includes(ch) ? draft.channels.filter((x) => x !== ch) : [...draft.channels, ch] })} />
                                        <label className="form-check-label" htmlFor={`cw-ch-${ch}`}>{CHANNEL_LABEL[ch]}{off ? " (customers only)" : ""}</label>
                                    </div>
                                );
                            })}
                        </div>
                        {draft.channels.length > 1 && (
                            <div className="mb-3">
                                <select className="form-select form-select-sm w-auto" value={draft.channelPriority} onChange={(e) => setDraft({ channelPriority: e.target.value })} aria-label="How many channels per person">
                                    <option value="all">Send on every channel each person can get</option>
                                    <option value="first-available">Send on the first channel each person can get (in the order shown)</option>
                                </select>
                            </div>
                        )}
                        <span className="form-label small d-block">Languages (the first is the main one; others fall back to it)</span>
                        <div className="d-flex flex-wrap gap-3 mb-3">
                            {(languages.length ? languages : [{ code: "en", name: "English" }]).map((l) => (
                                <div className="form-check" key={l.code}>
                                    <input className="form-check-input" type="checkbox" id={`cw-lang-${l.code}`} checked={draft.languages.includes(l.code)} onChange={() => setDraft({ languages: draft.languages.includes(l.code) ? draft.languages.filter((x) => x !== l.code) : [...draft.languages, l.code] })} />
                                    <label className="form-check-label" htmlFor={`cw-lang-${l.code}`}>{l.name}{draft.languages[0] === l.code ? " (main)" : ""}</label>
                                </div>
                            ))}
                        </div>
                        <label className="form-label small" htmlFor="cw-link">Link for {"{link}"} (optional, https://)</label>
                        <input id="cw-link" className="form-control" maxLength={500} value={draft.link?.url || ""} onChange={(e) => setDraft({ link: { url: e.target.value } })} placeholder="https://getmydrive.in/offers" />
                        <div className="form-text">Each person gets their own short link, so clicks are counted.</div>
                    </>
                )}

                {step === 2 && (campaignId ? <MessagesPanel campaignId={campaignId} /> : <Skeleton height={120} />)}

                {step === 3 && (
                    staff ? <div className="small text-muted">Coupons are for customers.</div> : (
                        <>
                            <label className="form-label small" htmlFor="cw-coupon">Attach a coupon (optional)</label>
                            <select id="cw-coupon" className="form-select mb-2" value={draft.coupon?.couponCode || ""} onChange={(e) => setDraft({ coupon: e.target.value ? { couponCode: e.target.value } : null })}>
                                <option value="">No coupon</option>
                                {(coupons || []).map((c) => <option key={c.couponCode} value={c.couponCode}>{c.couponCode} - {c.offer}{c.targeted ? " (given to each person who gets it)" : ""}</option>)}
                            </select>
                            {coupon ? <div className="small">Use {"{couponCode}"}, {"{couponOffer}"} and {"{couponExpiry}"} in the messages{coupon.offer ? `: ${coupon.couponCode}, ${coupon.offer}` : ""}.</div>
                                : <div className="small text-muted">Only switched-on coupons that haven't expired or run out are listed.</div>}
                        </>
                    )
                )}

                {step === 4 && (
                    <>
                        <div className="d-flex flex-wrap gap-2 mb-3" role="radiogroup" aria-label="When">
                            {[["now", "Send now"], ["once", "At a time"], ["recurring", "Repeat"], ...(staff ? [] : [["trigger", "When something happens"]])].map(([key, label]) => (
                                <button key={key} type="button" role="radio" aria-checked={s.type === key} className={`nc-pill ${s.type === key ? "is-active" : ""}`} onClick={() => setSchedule({ type: key })}>{label}</button>
                            ))}
                        </div>
                        {s.type === "once" && (
                            <div className="mb-3" style={{ maxWidth: 280 }}>
                                <label className="form-label small" htmlFor="cw-at">Send at</label>
                                <input id="cw-at" type="datetime-local" className="form-control" value={toLocalInput(s.at)} onChange={(e) => setSchedule({ at: fromLocalInput(e.target.value) })} />
                            </div>
                        )}
                        {s.type === "recurring" && (
                            <div className="row g-3 mb-3">
                                <div className="col-md-3">
                                    <label className="form-label small" htmlFor="cw-freq">Repeat</label>
                                    <select id="cw-freq" className="form-select" value={s.recurrence.freq || "weekly"} onChange={(e) => setSchedule({ recurrence: { ...s.recurrence, freq: e.target.value, byDay: e.target.value === "monthly" ? [1] : e.target.value === "weekly" ? [1] : [] } })}>
                                        <option value="daily">Every day</option>
                                        <option value="weekly">Every week</option>
                                        <option value="monthly">Every month</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small" htmlFor="cw-time">At (India time)</label>
                                    <input id="cw-time" type="time" className="form-control" value={s.recurrence.time || ""} onChange={(e) => setSchedule({ recurrence: { ...s.recurrence, time: e.target.value } })} />
                                </div>
                                {s.recurrence.freq === "weekly" && (
                                    <div className="col-md-7">
                                        <span className="form-label small d-block">On</span>
                                        <div className="d-flex flex-wrap gap-2">
                                            {DAY_NAMES.map((name, d) => (
                                                <div className="form-check" key={name}>
                                                    <input className="form-check-input" type="checkbox" id={`cw-d${d}`} checked={(s.recurrence.byDay || []).includes(d)} onChange={() => { const days = s.recurrence.byDay || []; setSchedule({ recurrence: { ...s.recurrence, byDay: days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort() } }); }} />
                                                    <label className="form-check-label small" htmlFor={`cw-d${d}`}>{name}</label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {s.recurrence.freq === "monthly" && (
                                    <div className="col-md-4">
                                        <label className="form-label small" htmlFor="cw-mdays">Days of the month (1-28, commas)</label>
                                        <input id="cw-mdays" className="form-control" value={(s.recurrence.byDay || []).join(", ")} onChange={(e) => setSchedule({ recurrence: { ...s.recurrence, byDay: e.target.value.split(/[,\s]+/).map(Number).filter((n) => Number.isInteger(n) && n > 0) } })} />
                                    </div>
                                )}
                                <div className="col-md-4">
                                    <label className="form-label small" htmlFor="cw-from">Starting (optional)</label>
                                    <input id="cw-from" type="datetime-local" className="form-control" value={toLocalInput(s.at)} onChange={(e) => setSchedule({ at: fromLocalInput(e.target.value) })} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small" htmlFor="cw-until">Until (optional)</label>
                                    <input id="cw-until" type="datetime-local" className="form-control" value={toLocalInput(s.endsAt)} onChange={(e) => setSchedule({ endsAt: fromLocalInput(e.target.value) })} />
                                </div>
                            </div>
                        )}
                        {s.type === "trigger" && (
                            <div className="row g-3 mb-3">
                                <div className="col-md-5">
                                    <label className="form-label small" htmlFor="cw-trigger">When</label>
                                    <select id="cw-trigger" className="form-select" value={s.trigger.event || "inactive_days"} onChange={(e) => setSchedule({ trigger: { ...s.trigger, event: e.target.value } })}>
                                        {Object.entries(TRIGGER_LABEL).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small" htmlFor="cw-tdays">N (days)</label>
                                    <input id="cw-tdays" type="number" min={1} max={365} className="form-control" value={s.trigger.days ?? ""} onChange={(e) => setSchedule({ trigger: { ...s.trigger, days: e.target.value === "" ? null : Number(e.target.value) } })} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small" htmlFor="cw-cool">Again after (days, optional)</label>
                                    <input id="cw-cool" type="number" min={1} max={365} className="form-control" value={s.trigger.coolDownDays ?? ""} onChange={(e) => setSchedule({ trigger: { ...s.trigger, coolDownDays: e.target.value === "" ? null : Number(e.target.value) } })} />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small" htmlFor="cw-ttime">Checked daily at</label>
                                    <input id="cw-ttime" type="time" className="form-control" value={s.recurrence?.time || "10:00"} onChange={(e) => setSchedule({ recurrence: { ...s.recurrence, time: e.target.value } })} />
                                </div>
                                <div className="col-12 small text-muted">Each person gets it once{s.trigger.coolDownDays ? `, and again after ${s.trigger.coolDownDays} days` : ""}.</div>
                            </div>
                        )}
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label small" htmlFor="cw-tries">Tries per message</label>
                                <input id="cw-tries" type="number" min={1} max={10} className="form-control" value={draft.retry.maxAttempts} onChange={(e) => setDraft({ retry: { ...draft.retry, maxAttempts: Number(e.target.value) } })} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small" htmlFor="cw-wait">Minutes before trying again</label>
                                <input id="cw-wait" type="number" min={1} max={1440} className="form-control" value={draft.retry.backoffMinutes} onChange={(e) => setDraft({ retry: { ...draft.retry, backoffMinutes: Number(e.target.value) } })} />
                            </div>
                            {!staff && (
                                <div className="col-md-3">
                                    <label className="form-label small" htmlFor="cw-attr">Count bookings for (days)</label>
                                    <input id="cw-attr" type="number" min={1} max={90} className="form-control" value={draft.attributionDays} onChange={(e) => setDraft({ attributionDays: Number(e.target.value) })} />
                                </div>
                            )}
                            <div className="col-md-3 d-flex align-items-end">
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" id="cw-shadow" checked={!!draft.shadow} onChange={() => setDraft({ shadow: !draft.shadow })} />
                                    <label className="form-check-label small" htmlFor="cw-shadow">Shadow: build and log, send nothing</label>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {step === 5 && (
                    !review ? <Skeleton height={200} /> : (
                        <div>
                            <div className="mb-3"><span className="fw-semibold">{draft.name || "Untitled"}</span> <span className="text-muted small">· {scheduleText(draft.schedule)}{draft.shadow ? " · shadow" : ""}</span></div>
                            {review.reach === null ? (
                                <div className="small text-muted mb-3">A trigger campaign finds its people each day, so there is no count to show now.</div>
                            ) : review.reach?.error ? <div className="text-danger small mb-3">{review.reach.error}</div> : (
                                <div className="mb-3">
                                    <div className="fw-semibold mb-1">{review.reach.eligibleCount} of {review.reach.audienceCount} can be reached today</div>
                                    <table className="table table-sm w-auto">
                                        <tbody>
                                            {Object.entries(review.reach.byChannel || {}).map(([ch, n]) => <tr key={ch}><th scope="row" className="fw-normal">{CHANNEL_LABEL[ch]}</th><td>{n}</td></tr>)}
                                        </tbody>
                                    </table>
                                    {Object.keys(review.reach.excluded || {}).length > 0 && (
                                        <>
                                            <div className="small fw-semibold">Left out</div>
                                            <ul className="small mb-2">{Object.entries(review.reach.excluded).map(([r, n]) => <li key={r}>{review.reach.reasons?.[r] || reasonText(r)}: {n}</li>)}</ul>
                                        </>
                                    )}
                                    {Object.entries(review.reach.skipped || {}).map(([ch, reasons]) => (
                                        <div key={ch} className="small text-muted">{CHANNEL_LABEL[ch]} not sent to: {Object.entries(reasons).map(([r, n]) => `${review.reach.reasons?.[r] || reasonText(r)} (${n})`).join(", ")}</div>
                                    ))}
                                    {review.reach.sms && !review.reach.sms.open ? <div className="small text-muted">Promotional SMS waits for 9 AM.</div> : null}
                                    {review.reach.email?.overToday ? <div className="small text-muted">{review.reach.email.overToday} emails are over today's budget and go tomorrow.</div> : null}
                                </div>
                            )}
                            {review.overlap.map((w) => <div key={w.campaignId} className="alert alert-light border py-2 small">{w.message}</div>)}
                            {review.problems.length > 0 ? (
                                <div className="nc-problem mb-3" role="alert">
                                    <div className="fw-semibold">Fix before starting:</div>
                                    {review.problems.map((p) => <div key={`${p.channel}${p.language}${p.message}`}>{CHANNEL_LABEL[p.channel]} ({p.language}): {p.message}</div>)}
                                </div>
                            ) : <div className="small text-success mb-3">The messages are ready. Send yourself a test from the Messages step before starting.</div>}
                            <div className="d-flex flex-wrap gap-2">
                                <button type="button" className="btn btn-primary" onClick={start} disabled={busy || review.problems.length > 0 || status !== "draft"}>
                                    {draft.schedule.type === "now" ? "Start now" : "Schedule"}
                                </button>
                                <button type="button" className="btn btn-outline-secondary" onClick={async () => { if (await save()) toast.success("Saved as a draft"); }} disabled={busy || !editable}>Save as draft</button>
                            </div>
                            {status !== "draft" && <div className="small text-muted mt-2">This campaign is {status}: start, pause and resume it from its page.</div>}
                        </div>
                    )
                )}
            </fieldset>

            <div className="cw-foot">
                <button type="button" className="btn btn-outline-secondary" onClick={() => goTo(step - 1)} disabled={busy || step === 0}>Back</button>
                {step < STEPS.length - 1 && <button type="button" className="btn btn-primary" onClick={() => goTo(step + 1)} disabled={busy}>{busy ? "Saving..." : "Next"}</button>}
            </div>
        </div>
    );
};

export default CampaignWizard;
